// React
import React, { useState, useRef } from "react";

// Libraries
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckDouble, faCheck, faClock } from "@fortawesome/free-solid-svg-icons";
import { Button, Icon, Popup, Menu } from "semantic-ui-react";
import { get, isEmpty, includes } from "lodash";

// Redux
import { connect } from "react-redux";
import { setReply } from "@Actions/chats";
import downloadFile from "@Actions/downloads";

// Utils
import { formatDate } from "@Utils/formats";

// Components
import PrintRender from "@Components/PrintRender";
import Text from "@Components/Text";
import ReplyMessage from "../ReplyMessage";
import TextContent from "./Subcomponents/TextContent";
import ImageContent from "./Subcomponents/ImageContent";
import AudioContent from "./Subcomponents/AudioContent";
import VideoContent from "./Subcomponents/VideoContent";
import LocationContent from "./Subcomponents/LocationContent";
import DocumentContent from "./Subcomponents/DocumentContent";
import EmailContent from "./Subcomponents/EmailContent";
import CallContent from "./Subcomponents/CallContent";
import ContactContent from "./Subcomponents/ContactContent";
import FlowContent from "./Subcomponents/FlowContent";
import NoteContent from "./Subcomponents/NoteContent";

// Styles
import styles from "./styles.module.css";

const MessageCard = ({
	dispatch,
	style,
	item,
	isSearched = false,
	handleFromSearch,
	handleShare,
	isChatDisabled,
	companyName,
	userNames,
	platformTypes,
	// clientName,
	platformName,
}) => {
	const [optionsVisible, setOptionsVisible] = useState(false);
	const [optionsOpen, setOptionsOpen] = useState(false);
	const [printAction, setPrintAction] = useState(false);
	const printRef = useRef(null);

	const sentByAgent = get(item, "sentByAgent", false);
	const userName = get(item, "userName", "");
	const messageType = get(item, "type", null);
	const timestamp = get(item, "timestamp", 0);
	const messageStatus = get(item, "status", "read");
	const isNote = messageType === "Note";

	const isFlowFormResponse =
		messageType === "JSON" &&
		(() => {
			try {
				const parsed = JSON.parse(item.text || "{}");
				return parsed?.type === "form";
			} catch (e) {
				return false;
			}
		})();
	// const contactName = clientName || get(item, "contact.name", "") || get(item, "contactName", "");
	const channelName = platformName || get(item, "channel.label", "") || get(item, "platform.name", "");

	const onMouseEnter = () => {
		setOptionsVisible(true);
	};

	const onMouseLeave = () => {
		setOptionsVisible(false);
	};

	const onOpenOptions = () => {
		setOptionsOpen(true);
	};

	const onCloseOptions = () => {
		setOptionsOpen(false);
	};

	const handleReply = () => {
		dispatch(setReply(item));
		setOptionsOpen(false);
	};

	const handleCopy = () => {
		navigator.clipboard.writeText(item.text);
		setOptionsOpen(false);
	};

	const handleDownload = async () => {
		const link = document.createElement("a");
		const url = await dispatch(downloadFile({ uri: item.uri, withLoading: true }));
		const tmp = item.uri.split("/");
		const fileName = tmp[tmp.length - 1];
		link.href = url;
		link.download = fileName;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
		setOptionsOpen(false);
	};

	const handlePrint = () => {
		setPrintAction(true);
		setOptionsOpen(false);
	};

	const handleSharing = () => {
		handleShare(item);
		setOptionsOpen(false);
	};

	const renderMessageContent = (type) => {
		switch (type) {
			case "Text":
			case "Interactive":
				return <TextContent item={item} />;
			case "Image":
				return <ImageContent item={item} />;
			case "Note":
				return <NoteContent item={item} />;
			case "Audio":
				return <AudioContent item={item} />;
			case "Video":
				return <VideoContent item={item} />;
			case "Location":
				return <LocationContent item={item} />;
			case "Document":
				return <DocumentContent item={item} onClick={handleDownload} />;
			case "Email":
				return <EmailContent item={item} />;
			case "Call":
				return <CallContent item={item} />;
			case "Contact":
				return <ContactContent item={item} />;
			case "JSON":
				return <FlowContent item={item} />;
			default:
				return null;
		}
	};

	const renderIcon = (status) => {
		switch (status) {
			case "local":
				return <FontAwesomeIcon className={styles.icon} icon={faClock} />;
			case "sent":
				return <FontAwesomeIcon className={styles.icon} icon={faCheck} />;
			case "read":
				return <FontAwesomeIcon className={styles.icon} icon={faCheckDouble} />;
			default:
				return null;
		}
	};

	const getAlignmentClass = () => {
		if (isNote) return styles.centerContainer;
		return sentByAgent ? styles.rightContainer : styles.leftContainer;
	};

	const getBgClass = () => {
		if (isNote) return styles.yellowBg;
		return sentByAgent ? styles.greenBg : styles.whiteBg;
	};

	const getPrintBgClass = () => {
		if (isNote) return styles.printYellowBg;
		return sentByAgent ? styles.printGreenBg : styles.printWhiteBg;
	};

	const getFabBgClass = () => {
		if (isNote) return styles.yellowFabBg;
		return sentByAgent ? styles.greenFabBg : styles.whiteFabBg;
	};

	if (!renderMessageContent(messageType)) return null;
	return (
		<div
			style={style}
			className={`${styles.container} ${getAlignmentClass()} ${isSearched ? styles.searched : ""}`}
			onClick={isSearched ? handleFromSearch : undefined}
			role="button"
			tabIndex={-1}>
			<div ref={printRef} className={styles.printBubble}>
				{messageType === "Text" ? (
					<div className={styles.printPageContainer}>
						{/* Título "Mensaje" */}
						{/* <div className={styles.printTitle}>
							<Text className={styles.printTitleText}>Mensaje</Text>
						</div> */}

						{/* Información del cliente y canal */}
						{/* 
						{(contactName || channelName) && (
							<div className={styles.printClientInfo}>
								{contactName && <Text className={styles.printClientName}>{contactName}</Text>}
								{channelName && <Text className={styles.printChannelName}>{channelName}</Text>}
							</div>
						)} 
						*/}

						{/* Burbuja del mensaje */}
						<div className={`${styles.printMessageBox} ${getPrintBgClass()}`}>
							{sentByAgent && (
								<div className={styles.printAgentContainer}>
									<Text className={styles.printUserText}>{userName}</Text>
								</div>
							)}
							{!isEmpty(item.reply) && !isFlowFormResponse && (
								<div className={styles.printReplyContainer}>
									<ReplyMessage item={item.reply} />
								</div>
							)}
							<div className={styles.printContent}>{renderMessageContent(messageType)}</div>
							<div
								className={styles.printInfoContainer}
								style={{ justifyContent: !sentByAgent && channelName ? "space-between" : "flex-end" }}>
								{!sentByAgent && channelName && (
									<div className={styles.printChannelContainer}>
										<Text className={styles.printChannelText}>{channelName}</Text>
									</div>
								)}
								<Text className={styles.printTimeText}>{formatDate(timestamp, "h:mm A")}</Text>
							</div>
						</div>
					</div>
				) : (
					<div className={`${styles.printMessageBox} ${getPrintBgClass()}`}>
						{sentByAgent && (
							<div className={styles.printAgentContainer}>
								<Text className={styles.printUserText}>{userName}</Text>
							</div>
						)}
						{!isEmpty(item.reply) && !isFlowFormResponse && (
							<div className={styles.printReplyContainer}>
								<ReplyMessage item={item.reply} />
							</div>
						)}
						<div className={styles.printContent}>{renderMessageContent(messageType)}</div>
						<div
							className={styles.printInfoContainer}
							style={{ justifyContent: !sentByAgent && channelName ? "space-between" : "flex-end" }}>
							{!sentByAgent && channelName && (
								<div className={styles.printChannelContainer}>
									<Text className={styles.printChannelText}>{channelName}</Text>
								</div>
							)}
							<Text className={styles.printTimeText}>{formatDate(timestamp, "h:mm A")}</Text>
						</div>
					</div>
				)}
			</div>

			<div
				className={`${styles.messageBox} ${getBgClass()}`}
				onMouseEnter={!isSearched ? onMouseEnter : undefined}
				onMouseLeave={!isSearched ? onMouseLeave : undefined}>
				<div className={styles.topContainer}>
					{sentByAgent && (
						<div className={styles.agentContainer}>
							<Text className={styles.userText}>{isNote ? `Nota · ${userName}` : userName}</Text>
						</div>
					)}
					{(optionsVisible || optionsOpen) && !includes(["Call"], messageType) && (
						<Popup
							className={styles.menu}
							on="click"
							open={optionsOpen}
							position="bottom left"
							onClose={onCloseOptions}
							trigger={
								<Button className={`${styles.fab} ${getFabBgClass()}`} icon circular onClick={onOpenOptions}>
									<Icon className={styles.fabIcon} name="angle down" />
								</Button>
							}
							content={
								<Menu vertical className={styles.menuVertical}>
									{!isNote && !isChatDisabled && includes(platformTypes, "Reply") && (
										<Menu.Item className={styles.menuItem} onClick={handleReply}>
											<Icon className={styles.menuIcon} name="reply" />
											<Text className={styles.menuText}>Responder</Text>
										</Menu.Item>
									)}
									{!isNote && (
										<Menu.Item className={styles.menuItem} onClick={handleSharing}>
											<Icon className={styles.menuIcon} name="share square" />
											<Text className={styles.menuText}>Reenviar</Text>
										</Menu.Item>
									)}
									{!isEmpty(item.text) && (
										<Menu.Item className={styles.menuItem} onClick={handleCopy}>
											<Icon className={styles.menuIcon} name="copy" />
											<Text className={styles.menuText}>Copiar Texto</Text>
										</Menu.Item>
									)}
									{includes(["Audio", "Video", "Image", "Document", "Note"], messageType) && !isEmpty(item.uri) && (
										<Menu.Item className={styles.menuItem} onClick={handleDownload}>
											<Icon className={styles.menuIcon} name="download" />
											<Text className={styles.menuText}>Descargar</Text>
										</Menu.Item>
									)}
									{includes(["Image", "Text", "Note"], messageType) && (
										<Menu.Item className={styles.menuItem} onClick={handlePrint}>
											<Icon className={styles.menuIcon} name="print" />
											<Text className={styles.menuText}>Imprimir</Text>
										</Menu.Item>
									)}
								</Menu>
							}
						/>
					)}
				</div>
				{!isEmpty(item.reply) && !isFlowFormResponse && (
					<div className={styles.replyContainer}>
						<ReplyMessage item={item.reply} />
					</div>
				)}
				<div>{renderMessageContent(messageType)}</div>
				<div className={styles.infoContainer}>
					<Text className={styles.timeText}>{formatDate(timestamp, "h:mm A")}</Text>
					{renderIcon(messageStatus)}
				</div>
			</div>
			<PrintRender
				hiddenButon
				printAction={printAction}
				printActionRetry={() => {
					setPrintAction(false);
				}}
				printHeader={{ company: companyName, user: userNames }}
				renderData={printRef}
				hideClientInfo
			/>
		</div>
	);
};

MessageCard.propTypes = {
	dispatch: PropTypes.func,
	style: PropTypes.object,
	item: PropTypes.object,
	isSearched: PropTypes.bool,
	handleFromSearch: PropTypes.func,
	handleShare: PropTypes.func,
	isChatDisabled: PropTypes.bool,
	companyName: PropTypes.string,
	userNames: PropTypes.string,
	platformTypes: PropTypes.array,
	// clientName: PropTypes.string,
	platformName: PropTypes.string,
};

function mapStateToProps({ company, user }) {
	return {
		companyName: company.name,
		userNames: user.name,
	};
}

export default connect(mapStateToProps)(MessageCard);
